import * as React from 'react';
import { withRouter } from 'react-router-dom';

// Utils
import { getLocale } from '@shopee/global-env';
import { I18n } from '@shopee/platform';
import { withPopup } from '@shopee/rw-with-popup';
import { localStorage } from '@shopee/web-storage';
import { bodyScroll } from '@shopee/body-scroll';

import type { SearchParams, DisclaimerTypes } from '@shopee/search-common-util';
import type { ComponentWithPopupTypes } from '@shopee/rw-with-popup';

type ContextRouter = {
  match: any;
  history: any;
  location: any;
};

type OwnProps = {
  searchParams: SearchParams;
  originalSearchParams?: SearchParams;
  disclaimerInfo: Array<{
    disclaimer_types: DisclaimerTypes;
    triggered_by_keyword: boolean | null;
    triggered_catids: boolean | null;
  }> | null | void;
};

type PropTypes = ComponentWithPopupTypes & OwnProps & ContextRouter;

type State = {
  [type: number]: {
    [key: string]: boolean;
  };
};

const { t } = I18n;

const LOCALE = getLocale();

class DisclaimerPopup extends React.Component<PropTypes, State> {
  rejected = false;

  constructor(props: PropTypes) {
    super(props);

    this.state = {};

    if (LOCALSTORAGE_SUPPORTED) {
      this.state = localStorage.get(DISCLAIMER_LOCAL_STORAGE_KEY) || {};
    }
  }

  componentDidMount() {
    this.checkDisclaimerDisplay();
  }

  componentDidUpdate() {
    this.checkDisclaimerDisplay();
  }

  checkDisclaimerDisplay() {
    if (LOCALE === 'VN' && !this.rejected) {
      const { searchParams, originalSearchParams } = this.props;
      const disclaimerState: Record<DisclaimerTypes, unknown> =
        this.checkAgreeToDisclaimer(originalSearchParams || searchParams);

      const types = Object.values(DISCLAIMER_TYPE);
      let i = 0,
        disclaimerType = null;
      while (disclaimerType === null && i < types.length) {
        const type: DisclaimerTypes = types[i] as DisclaimerTypes;
        if (!disclaimerState[type]) {
          disclaimerType = type;
        }
        i++;
      }

      if (disclaimerType !== null) {
        this.showDisclaimerPopup(disclaimerType);
      }
    }
  }

  showDisclaimerPopup = (disclaimerType: DisclaimerTypes) => {
    this.props.showPopup({
      message: t(DISCLAIMER_MESSAGE_MAP[disclaimerType]),
      mainButtonLabel: t('label_agree_search_disclaimer'),
      secondaryButtonLabel: t('label_disagree_search_disclaimer'),
      onClickMainButton: () => {
        this.hideDisclaimerPopup(disclaimerType);
      },
      onClickSecondaryButton: this.goBackDisclaimerPopup,
      onClickOverlay: this.goBackDisclaimerPopup,
    });
  };

  hideDisclaimerPopup = (disclaimerType: DisclaimerTypes) => {
    const { searchParams, originalSearchParams } = this.props;
    this.setAgreeToDisclaimer(disclaimerType, searchParams);
    if (originalSearchParams) {
      this.setAgreeToDisclaimer(disclaimerType, originalSearchParams);
    }
    this.props.hidePopup();
  };

  goBackDisclaimerPopup = () => {
    this.rejected = true;

    const { history } = this.props;
    const hasPrevLocation = history.action === 'PUSH';
    if (hasPrevLocation) {
      history.goBack();
    } else {
      history.push('/');
    }
    // NOTE: Need to manually resume bodyScroll because popup component is already unmounted when route changes
    bodyScroll.resume();
  };

  render() {
    return this.props.popupElement;
  }

  setAgreeToDisclaimer = (
    disclaimerType: DisclaimerTypes,
    searchParams: SearchParams
  ) => {
    const disclaimer: Record<string, boolean> = {};
    if (searchParams.keyword) {
      disclaimer[searchParams.keyword] = true;
    }
    if (searchParams.category) {
      disclaimer[searchParams.category] = true;
    }
    if (searchParams.subcategory) {
      disclaimer[searchParams.subcategory] = true;
    }
    if (Array.isArray(searchParams.facet)) {
      searchParams.facet.forEach(catId => {
        disclaimer[catId] = true;
      });
    }

    this.setState(
      prevState => ({
        [disclaimerType]: {
          ...prevState[disclaimerType],
          ...disclaimer,
        },
      }),
      () => {
        if (LOCALSTORAGE_SUPPORTED) {
          localStorage.set(DISCLAIMER_LOCAL_STORAGE_KEY, this.state);
        }
      }
    );
  };

  checkAgreeToDisclaimer = (
    searchParams: SearchParams
  ): Record<number, boolean> => {
    const { disclaimerInfo } = this.props;

    if (!disclaimerInfo) {
      return DEFAULT_DISCLAIMER_STATE;
    }

    const isAgreeToDisclaimer = { ...DEFAULT_DISCLAIMER_STATE };

    const { keyword } = searchParams;
    disclaimerInfo.forEach(disclaimer => {
      const type = disclaimer.disclaimer_types;
      const disclaimerState = this.state[type];

      let categoryKeys: Array<string | number> = [];
      if (disclaimer.triggered_by_keyword && keyword) {
        isAgreeToDisclaimer[type] =
          !!disclaimerState && !!disclaimerState[keyword];
      }
      if (Array.isArray(disclaimer.triggered_catids)) {
        categoryKeys = categoryKeys.concat(disclaimer.triggered_catids);
      }
      if (categoryKeys.length) {
        isAgreeToDisclaimer[type] =
          isAgreeToDisclaimer[type] &&
          categoryKeys.every(
            key => !!disclaimerState && !!disclaimerState[key]
          );
      }
    });

    return isAgreeToDisclaimer;
  };
}

const LOCALSTORAGE_SUPPORTED = localStorage.isSupported();
const DISCLAIMER_LOCAL_STORAGE_KEY = '@shopee/search-disclaimer';
export const DISCLAIMER_TYPE = {
  ADVERTISE: 0,
  ALCOHOL: 1,
};
const DEFAULT_DISCLAIMER_STATE = {
  [DISCLAIMER_TYPE.ADVERTISE]: true,
  [DISCLAIMER_TYPE.ALCOHOL]: true,
};
const DISCLAIMER_MESSAGE_MAP = {
  [DISCLAIMER_TYPE.ADVERTISE]: 'text_search_disclaimer_msg',
  [DISCLAIMER_TYPE.ALCOHOL]: 'text_search_disclaimer_adult_msg',
};

export default withRouter(
  withPopup(DisclaimerPopup)
) as unknown as React.ComponentType<OwnProps>;
